import React, { useState, useEffect } from "react";
import { IPaymentMethod, IBusinessLinePackage } from "@/interfaces/Ibilling";
import {
  FiCheck,
  FiPlus,
  FiMoreVertical,
  FiMapPin,
  FiTrash2,
  FiCreditCard,
  FiClock,
  FiInfo,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertTriangle,
} from "react-icons/fi";
import ChangePlanDialog from "./ChangePlanDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useDeleteSubscriptionPackage } from "@/api/billingApi";
import { toast } from "react-toastify";

interface PlanTabProps {
  paymentMethods: IPaymentMethod[];
  packages: IBusinessLinePackage[];
  onSetDefault: (id: string) => void;
  onDelete: (id: string) => void;
  onAddCard: () => void;
  onChangePlan?: (
    oldPackageId: string,
    newPackageId: string,
    effectiveFrom: "immediately" | "next_billing_cycle"
  ) => Promise<any>;
}

const PlanTab: React.FC<PlanTabProps> = ({
  paymentMethods,
  packages,
  onSetDefault,
  onDelete,
  onAddCard,
  onChangePlan,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isMenuItemClicked, setIsMenuItemClicked] = useState(false);
  const [changePlanDialog, setChangePlanDialog] = useState<{
    isOpen: boolean;
    package: IBusinessLinePackage | null;
  }>({
    isOpen: false,
    package: null,
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    package: IBusinessLinePackage | null;
  }>({
    isOpen: false,
    package: null,
  });

  // Delete subscription package mutation
  const deleteSubscriptionPackageMutation = useDeleteSubscriptionPackage();

  // Debug effect to monitor dialog state changes
  useEffect(() => {
    console.log("Dialog state changed:", changePlanDialog);
  }, [changePlanDialog]);

  const toggleMenu = (id: string) => {
    const newValue = openMenuId === id ? null : id;
    setOpenMenuId(newValue);
    console.log("Toggle menu for id:", id);
    console.log("New open menu id:", newValue);
  };

  const closeMenu = () => {
    // Prevent closing menu if a menu item was just clicked (prevent race condition)
    if (isMenuItemClicked) {
      return;
    }
    setOpenMenuId(null);
  };

  const openChangePlanDialog = (pkg: IBusinessLinePackage) => {
    // Prevent multiple rapid clicks
    if (isMenuItemClicked) {
      return;
    }

    console.log("=== OPENING CHANGE PLAN DIALOG ===");
    console.log("Package:", pkg);
    console.log("Current changePlanDialog state:", changePlanDialog);

    // Set flag to prevent menu from closing due to onBlur
    setIsMenuItemClicked(true);

    // Set dialog state and close menu in the same batch
    const newState = {
      isOpen: true,
      package: pkg,
    };

    console.log("Setting changePlanDialog to:", newState);
    setChangePlanDialog(newState);
    setOpenMenuId(null);

    // Reset the flag after a short delay to allow normal menu operation
    setTimeout(() => {
      setIsMenuItemClicked(false);
    }, 150);

    console.log("=== DIALOG STATE SET, MENU CLOSED ===");
  };

  const closeChangePlanDialog = () => {
    setChangePlanDialog({
      isOpen: false,
      package: null,
    });
  };

  const openDeleteConfirmation = (pkg: IBusinessLinePackage) => {
    // Prevent multiple rapid clicks
    if (isMenuItemClicked) {
      return;
    }

    // Prevent deletion if this is the only active package
    if (isOnlyActivePackage(pkg.id)) {
      toast.warning("Cannot delete - At least one active package must exist");
      closeMenu();
      return;
    }

    // Set flag to prevent menu from closing due to onBlur
    setIsMenuItemClicked(true);

    // Set dialog state and close menu in the same batch
    setDeleteConfirmation({
      isOpen: true,
      package: pkg,
    });
    setOpenMenuId(null);

    // Reset the flag after a short delay to allow normal menu operation
    setTimeout(() => {
      setIsMenuItemClicked(false);
    }, 150);
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      package: null,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmation.package) {
      try {
        await deleteSubscriptionPackageMutation.mutateAsync(
          deleteConfirmation.package.id
        );
        // Call the parent's onDelete callback to update the UI
        onDelete(deleteConfirmation.package.id);
        closeDeleteConfirmation();
        toast.success("Subscription package deleted successfully");
      } catch (error: any) {
        console.error("Error deleting subscription package:", error);

        // Handle 400 Bad Request with specific error message
        if (error?.response?.status === 400) {
          const errorMessage =
            error?.response?.data?.detail ||
            "This business line already has a pending deactivation.";
          toast.error(errorMessage);
        } else {
          // Handle other errors
          const errorMessage =
            error?.response?.data?.detail ||
            error?.message ||
            "Failed to delete subscription package. Please try again.";
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleChangePlan = async (
    newPackageId: string,
    effectiveFrom: "immediately" | "next_billing_cycle"
  ) => {
    if (changePlanDialog.package && onChangePlan) {
      try {
        const response = await onChangePlan(
          changePlanDialog.package.id,
          newPackageId,
          effectiveFrom
        );
        // Return the response for the dialog to handle
        return response;
      } catch (error) {
        console.error("Error in plan change:", error);
        // Re-throw the error to let the dialog handle it
        throw error;
      }
    }
  };

  const getUsageColor = (used: number, total: number) => {
    const percentage = (used / total) * 100;
    if (percentage > 66) return "bg-red-500";
    if (percentage > 30) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getUsagePercentage = (used: number, total: number) => {
    return Math.min((used / total) * 100, 100);
  };

  const getCardBrandColor = (brand: string) => {
    const brandLower = brand.toLowerCase();
    switch (brandLower) {
      case "visa":
        return "bg-blue-600";
      case "mastercard":
        return "bg-red-600";
      case "amex":
        return "bg-green-600";
      default:
        return "bg-gray-600";
    }
  };

  const formatDeactivationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  // Check if this is the only active package (without pending deactivation)
  const isOnlyActivePackage = (currentPackageId: string) => {
    const activePackages = packages.filter(
      (pkg) => !pkg.pending_deactivation_date
    );
    return (
      activePackages.length === 1 && activePackages[0].id === currentPackageId
    );
  };

  return (
    <div className="w-full space-y-6 lg:space-y-8">
      {/* Cards Section */}
      <div>
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4 lg:mb-6">
          Payment Methods
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6 mb-6">
          {/* Payment Method Cards */}
          {paymentMethods.map((card) => (
            <div
              key={card.id}
              className={`relative bg-white border-2 rounded-xl p-4 lg:p-6 shadow-sm w-full  ${
                card.is_default
                  ? "border-green-200 bg-green-50/30"
                  : "border-gray-200"
              }`}
            >
              {/* Default Badge */}
              {card.is_default && (
                <div className="absolute -top-2 -right-2 bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full">
                  Default
                </div>
              )}

              {/* Card Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FiCreditCard className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700 font-medium">
                    {card.billing_details.name || "Unnamed Card"}
                  </span>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => toggleMenu(card.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    onBlur={(e) => {
                      // Only close menu if focus is not moving to a menu item
                      if (!isMenuItemClicked) {
                        const relatedTarget = e.relatedTarget as HTMLElement;
                        const isMenuItem =
                          relatedTarget?.closest('[role="menuitem"]');
                        if (!isMenuItem) {
                          setTimeout(closeMenu, 100);
                        }
                      }
                    }}
                    aria-label="Open menu"
                    title="Open menu"
                  >
                    <FiMoreVertical className="w-4 h-4 text-gray-500" />
                  </button>

                  {openMenuId === card.id && (
                    <div className="absolute right-0 bottom-full mb-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {!card.is_default && (
                        <button
                          role="menuitem"
                          onClick={() => {
                            onSetDefault(card.id);
                            closeMenu();
                          }}
                          className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                        >
                          <FiCheck className="w-4 h-4" />
                          <span>Set as default</span>
                        </button>
                      )}
                      <button
                        role="menuitem"
                        onClick={() => {
                          onDelete(card.id);
                          closeMenu();
                        }}
                        className="w-full px-4 py-3 text-left text-red-600 hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <FiTrash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Number */}
              <div className="text-2xl font-mono font-bold text-gray-900 mb-3">
                **** **** **** {card.card.last4}
              </div>

              {/* Card Details */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-gray-600 text-sm">
                    {card.card.brand.toUpperCase()} - Expires{" "}
                    {card.card.exp_month.toString().padStart(2, "0")}/
                    {card.card.exp_year}
                  </div>
                  <div
                    className={`w-8 h-5 ${getCardBrandColor(
                      card.card.brand
                    )} rounded flex items-center justify-center`}
                  >
                    <span className="text-white text-xs font-bold">
                      {card.card.brand.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Billing Address */}
                {card.billing_details.address.line1 && (
                  <div className="text-gray-500 text-xs pt-2 border-t border-gray-100">
                    <div className="font-medium text-gray-600 mb-1">
                      Billing Address:
                    </div>
                    <div>{card.billing_details.address.line1}</div>
                    {card.billing_details.address.line2 && (
                      <div>{card.billing_details.address.line2}</div>
                    )}
                    <div>
                      {card.billing_details.address.city &&
                        `${card.billing_details.address.city}, `}
                      {card.billing_details.address.state &&
                        `${card.billing_details.address.state} `}
                      {card.billing_details.address.postal_code}
                    </div>
                    <div>{card.billing_details.address.country}</div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Add New Card - Only show if less than 2 cards (max 2 cards allowed) */}
          {paymentMethods.length < 2 && (
            <div
              className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-4 lg:p-6 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-100 transition-colors w-full min-h-[200px]"
              onClick={onAddCard}
            >
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3">
                <FiPlus className="w-6 h-6 text-gray-500" />
              </div>
              <span className="text-gray-600 font-medium">Add new card</span>
            </div>
          )}
        </div>

        {/* Maximum Cards Reached Message */}
        {paymentMethods.length >= 2 && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <FiInfo className="text-blue-600 text-sm" />
              <span className="text-blue-800 text-sm font-medium">
                Maximum of 2 payment methods reached
              </span>
            </div>
            <p className="text-blue-700 text-sm mt-1">
              You can manage your existing payment methods or delete one to add
              a new one.
            </p>
          </div>
        )}

        {/* Billing Information */}
        <div className="text-gray-600 text-sm">
          Billed on 28th of every month. Next billing on 28 Dec 2024
        </div>
      </div>

      {/* Plan Details Section */}
      <div>
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4 lg:mb-6">
          Plan details
        </h2>

        <div className="w-full space-y-3 lg:space-y-4">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="w-full bg-white border border-gray-200 rounded-xl p-4 lg:p-6 shadow-sm"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {pkg.package.name}
                  </h3>

                  <div className="text-gray-600 mb-3">{pkg.phone_number}</div>

                  <div className="mb-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(
                          pkg.used_minutes,
                          pkg.package.minutes
                        )}`}
                        style={{
                          width: `${getUsagePercentage(
                            pkg.used_minutes,
                            pkg.package.minutes
                          )}%`,
                        }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium">
                        {pkg.call_count} Calls ({pkg.used_minutes} mins/
                        {pkg.package.minutes} mins)
                      </span>
                    </div>
                  </div>

                  {/* Pending Deactivation Section */}
                  {pkg.pending_deactivation_date && (
                    <div className="mt-4 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <FiAlertTriangle className="text-red-600 text-sm" />
                        <span className="text-red-800 font-semibold text-xs">
                          Pending Deactivation
                        </span>
                      </div>

                      <div className="space-y-1">
                        <div className="text-red-700 text-sm">
                          This package will be deactivated on{" "}
                          <span className="font-medium">
                            {formatDeactivationDate(
                              pkg.pending_deactivation_date
                            )}
                          </span>
                        </div>
                        <div className="text-red-600 text-xs">
                          No changes or deletions are allowed for packages with
                          pending deactivation.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pending Updated Package Section */}
                  {pkg.pending_updated_package &&
                    !pkg.pending_deactivation_date && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <FiClock className="text-blue-600 text-sm" />
                          <span className="text-blue-800 font-semibold text-xs">
                            Pending Plan Update
                          </span>
                          {/* Upgrade/Downgrade Indicator */}
                          {pkg.pending_updated_package.minutes >
                          pkg.package.minutes ? (
                            <FiTrendingUp
                              className="text-green-600 text-sm"
                              title="Upgrade"
                            />
                          ) : pkg.pending_updated_package.minutes <
                            pkg.package.minutes ? (
                            <FiTrendingDown
                              className="text-red-600 text-sm"
                              title="Downgrade"
                            />
                          ) : null}
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-blue-700 text-sm font-medium">
                              {pkg.pending_updated_package.name}
                            </span>
                            <span className="text-blue-800 font-bold text-sm">
                              ${pkg.pending_updated_package.price.toFixed(2)}
                            </span>
                          </div>

                          <div className="flex justify-between items-center text-xs text-blue-600">
                            <span>
                              {pkg.pending_updated_package.minutes} minutes
                              {pkg.pending_updated_package.minutes >
                              pkg.package.minutes ? (
                                <span className="text-green-600 ml-1">
                                  (+
                                  {pkg.pending_updated_package.minutes -
                                    pkg.package.minutes}
                                  )
                                </span>
                              ) : pkg.pending_updated_package.minutes <
                                pkg.package.minutes ? (
                                <span className="text-red-600 ml-1">
                                  (-
                                  {pkg.package.minutes -
                                    pkg.pending_updated_package.minutes}
                                  )
                                </span>
                              ) : null}
                            </span>
                            <span className="flex items-center space-x-1">
                              <FiInfo className="text-blue-500" />
                              <span>Effective next billing cycle</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                </div>

                <div className="flex items-center justify-between lg:justify-end space-x-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      ${pkg.package.price.toFixed(2)}
                    </div>
                  </div>

                  <div className="relative">
                    {!pkg.pending_deactivation_date ? (
                      <>
                        <button
                          type="button"
                          onClick={() => toggleMenu(pkg.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          onBlur={(e) => {
                            // Only close menu if focus is not moving to a menu item
                            if (!isMenuItemClicked) {
                              const relatedTarget =
                                e.relatedTarget as HTMLElement;
                              const isMenuItem =
                                relatedTarget?.closest('[role="menuitem"]');
                              if (!isMenuItem) {
                                setTimeout(closeMenu, 100);
                              }
                            }
                          }}
                          aria-label="Open menu"
                          title="Open menu"
                        >
                          <FiMoreVertical className="w-5 h-5 text-gray-500" />
                        </button>

                        {openMenuId === pkg.id && (
                          <div className="absolute right-0 bottom-full mb-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => openChangePlanDialog(pkg)}
                              className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                            >
                              <FiMapPin className="w-4 h-4" />
                              <span>Change Plan</span>
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => openDeleteConfirmation(pkg)}
                              disabled={isOnlyActivePackage(pkg.id)}
                              className={`w-full px-4 py-3 text-left flex items-center space-x-3 ${
                                isOnlyActivePackage(pkg.id)
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-red-600 hover:bg-gray-50"
                              }`}
                              title={
                                isOnlyActivePackage(pkg.id)
                                  ? "Cannot delete - At least one active package must exist"
                                  : "Delete package"
                              }
                            >
                              <FiTrash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className="p-2 text-gray-400"
                        title="Menu disabled - Package has pending deactivation"
                      >
                        <FiMoreVertical className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Change Plan Dialog */}
      {(() => {
        console.log("=== DIALOG RENDERING CHECK ===");
        console.log("changePlanDialog.isOpen:", changePlanDialog.isOpen);
        console.log("changePlanDialog.package:", changePlanDialog.package);
        console.log(
          "Should render dialog:",
          changePlanDialog.isOpen && changePlanDialog.package
        );
        return null;
      })()}

      {changePlanDialog.isOpen && changePlanDialog.package && (
        <ChangePlanDialog
          isOpen={changePlanDialog.isOpen}
          onClose={closeChangePlanDialog}
          currentPackage={changePlanDialog.package}
          availablePackages={packages}
          onConfirmChange={handleChangePlan}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.isOpen && deleteConfirmation.package && (
        <ConfirmationDialog
          isOpen={deleteConfirmation.isOpen}
          onClose={closeDeleteConfirmation}
          onConfirm={handleDeleteConfirm}
          title="Delete Subscription Package"
          message={`Are you sure you want to delete the subscription package for ${deleteConfirmation.package.phone_number}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={deleteSubscriptionPackageMutation.isPending}
          variant="danger"
        />
      )}
    </div>
  );
};

export default PlanTab;
